import '.a{display:flex;}.ssr-plugin'
import '.c2{flex-direction:column-reverse;}.ssr-plugin'
import '.ab{align-items:stretch;}.ssr-plugin'
import '.d{gap:2rem;}.ssr-plugin'
import '.c3{padding:1.5rem;}.ssr-plugin'
import '.c4{overflow-y:scroll;}.ssr-plugin'
import '.ax{flex:1 1 0;}.ssr-plugin'
import '.ay{min-width:0px;}.ssr-plugin'
import '.g{justify-content:center;}.ssr-plugin'
import '.c{align-items:center;}.ssr-plugin'
import '.a0{width:100%;}.ssr-plugin'
import '.bj{height:100%;}.ssr-plugin'
import '.c5{cursor:zoom-out;}.ssr-plugin'
import '.c6{background-color:var(--shades-blue-50);}.ssr-plugin'
import '.b9{border:none;}.ssr-plugin'
import '.c7{border-top:var(--blue-border);}.ssr-plugin'
import '.bg{margin-top:2rem;}.ssr-plugin'
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import { useAuthState, useClient } from '@based/sdk/react'
import { useTranslations } from '@i18n/useLanguage'
import { fullName } from '@app/util/helpers'
import useMediaQuery from '@app/util/useMediaQuery'

import { Title } from '@app/components/core/typography/Title'
import { Text } from '@app/components/core/typography/Text'
import { Avatar } from '@app/components/core/avatar/Avatar'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { CloseButton } from '@app/components/core/button/CloseButton'
import useDateFormat from '@app/util/useDateFormat'
import { TextInput } from '@app/components/core/input/text/TextInput'

import { Result } from 'functions/app/dashboard/communications/get-messages'
import getMessagesFunction from 'functions/app/dashboard/communications/get-messages/based.config'
import { useToast } from '@app/util/hooks/useToast'
import { styled } from 'inlines'
import { FileDisplay } from '@app/components/core/fileDisplay/FileDisplay'
import { ModalWrapper } from '@app/components/blocks/modal/ModalWrapper'
import { Image } from '@app/components/core/image/Image'

type Message = {
  id: number
  authorName: string
  authorAvatar?: string
  creationDate: Date
  content: string
  isUnread: boolean
  files?: {
    id: number
    name: string
    src: string
    mimeType: string
  }[]
}

export type ConversationPanelProps = {
  conversationId: number
  conversationTitle: string
  unread: boolean
  onClose: VoidFunction
}

export const ConversationPanel = ({
  conversationId,
  conversationTitle,
  unread,
  onClose,
}: ConversationPanelProps) => {
  const t = useTranslations().app.dashboard.messages
  const isMobile = useMediaQuery()
  const client = useClient()
  const { userId } = useAuthState()
  const { showFailToast } = useToast()

  const [newMessage, setNewMessage] = useState<string>('')
  const [files, setFiles] = useState<File[]>([])
  const [messages, setMessages] = useState<Message[]>([])
  const [isSending, setIsSending] = useState(false)
  const isSendingRef = useRef(false)
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const messageRefs = useRef<Map<number, HTMLDivElement>>(new Map())
  const hasScrolled = useRef(false)

  useEffect(() => {
    hasScrolled.current = false
    messageRefs.current.clear()
  }, [conversationId])

  useEffect(() => {
    if (!userId) {
      return
    }

    const unsub = client
      .query(getMessagesFunction.name, {
        userId,
        channelId: conversationId,
      })
      .subscribe((res: Result) => {
        setMessages(
          res.map((m) => ({
            id: m.id,
            authorName: fullName(m.sender),
            authorAvatar: m.sender?.avatar?.src,
            creationDate: new Date(m.createdAt),
            content: m.content,
            isUnread:
              m.recipients.length > 0 && m.recipients[0].$status === 'new',
            files: m.files,
          })),
        )
      })

    return () => {
      unsub()
    }
  }, [conversationId, client, userId])

  useEffect(() => {
    if (hasScrolled.current || messages.length === 0) {
      return
    }
    hasScrolled.current = true

    // Messages are sorted desc (newest first). Find the oldest unread message
    // which is the last unread in the array.
    let targetId: number | undefined
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].isUnread) {
        targetId = messages[i].id
        break
      }
    }
    // If no unread messages, scroll to the newest message
    if (targetId === undefined) {
      targetId = messages[0].id
    }

    requestAnimationFrame(() => {
      const container = messagesContainerRef.current
      const el = messageRefs.current.get(targetId)
      if (container && el) {
        container.scrollTop = el.offsetTop - container.offsetTop
      }
    })
  }, [messages])

  const canSend = (newMessage.trim() !== '' || files.length > 0) && !isSending

  const send = useCallback(() => {
    if (isSendingRef.current) {
      return
    }

    if (newMessage.trim() === '' && files.length === 0) {
      return
    }

    isSendingRef.current = true
    setIsSending(true)
    client
      .call('send-message', {
        conversationId,
        message: newMessage,
      })
      .then(async (messageId) => {
        if (!messageId) {
          showFailToast()
          isSendingRef.current = false
          setIsSending(false)
          return
        }

        if (files.length > 0) {
          const uploads = files.map((file) =>
            client.stream('upload-file', {
              mimeType: file.type,
              contents: file,
              size: file.size,
              fileName: `${file.name}-${Date.now()}`,
              payload: { messageFile: { id: messageId } },
            }),
          )
          await Promise.all(uploads)
        }

        setNewMessage('')
        setFiles([])
        isSendingRef.current = false
        setIsSending(false)
      })
      .catch((error) => {
        console.error(error)
        showFailToast()
        isSendingRef.current = false
        setIsSending(false)
      })
  }, [conversationId, newMessage, files, client, showFailToast])

  useEffect(() => {
    const id = conversationId
    const wasUnread = unread

    return () => {
      if (wasUnread) {
        client.call('read-message', { conversationId: id }).catch(() => {})
      }
    }
  }, [conversationId, unread, client])

  return (
    <Column
      alignItems="stretch"
      gap={0}
      style={{
        backgroundColor: 'var(--shades-white)',
        borderRadius: '1.25rem',
        flex: isMobile ? '1 1 auto' : '1 1 30rem',
        minWidth: isMobile ? 0 : '30rem',
        height: '80vh',
        position: 'sticky',
        top: '8rem',
      }}
    >
      <Row
        gap="0.5rem"
        style={{
          padding: '1.5rem',
          justifyContent: 'space-between',
          borderBottom: 'var(--gray-border)',
        }}
      >
        <Title.H5
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {conversationTitle}
        </Title.H5>
        <CloseButton onClick={onClose} size="small" />
      </Row>

      <div className="a c2 ab d c3 c4 ax ay"
        ref={messagesContainerRef}
      >
        {messages.map((message, i) => (
          <MessageBlock
            key={message.id}
            isFirst={i === 0}
            ref={(el) => {
              if (el) {
                messageRefs.current.set(message.id, el)
              } else {
                messageRefs.current.delete(message.id)
              }
            }}
            onImageClick={setLightboxSrc}
            {...message}
          />
        ))}
      </div>
      {lightboxSrc && (
        <ModalWrapper>
          <div className="a g c a0 bj c5"
            onClick={() => setLightboxSrc(null)}
          >
            <Image
              src={lightboxSrc}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              resizeWidth={isMobile ? 800 : 1920}
              style={{
                maxWidth: '90vw',
                maxHeight: '90vh',
                objectFit: 'contain',
                borderRadius: '0.5rem',
                cursor: 'default',
              }}
            />
          </div>
        </ModalWrapper>
      )}
      <Column
        alignItems="stretch"
        gap={'0.5rem'}
        style={{
          padding: '1.5rem',
          flexGrow: 0,
        }}
      >
        <TextInput
          placeholder={t.typeYourReply}
          text={newMessage}
          setText={setNewMessage}
          onSubmit={canSend ? send : undefined}
          disabled={isSending}
          fileSelector={{
            maxFiles: 5,
            maxSizeMB: 10,
            allowedFileTypes: [
              'image/jpeg',
              'image/png',
              'image/gif',
              'image/webp',
              'application/pdf',
              'application/msword',
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
              'application/vnd.ms-excel',
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              'application/vnd.ms-powerpoint',
              'application/vnd.openxmlformats-officedocument.presentationml.presentation',
            ],
            onFileSelectionUpdated: (files) => setFiles(files),
            disabled: isSending,
          }}
        />
        <Row
          gap={'0.5rem'}
          style={{
            alignItems: 'center',
            overflowX: 'scroll',
          }}
        >
          {files.map((file, i) => (
            <FileDisplay
              key={i}
              type={'document'}
              name={file.name}
              onRemove={
                isSending
                  ? undefined
                  : () => setFiles((prev) => prev.toSpliced(i, 1))
              }
              isLoading={isSending}
            />
          ))}
        </Row>
      </Column>
    </Column>
  )
}

type MessageProps = {
  isFirst: boolean
  onImageClick: (src: string) => void
} & Message

const MessageBlock = forwardRef<HTMLDivElement, MessageProps>(
  (
    {
      authorName,
      authorAvatar,
      creationDate,
      content,
      files,
      isFirst,
      onImageClick,
    },
    ref,
  ) => {
    const dateFormat = useDateFormat(false, true)

    const imageFiles =
      files?.filter((f) => f.mimeType.startsWith('image/')) ?? []
    const documentFiles =
      files?.filter((f) => !f.mimeType.startsWith('image/')) ?? []

    return (
      <styled.div ref={ref}>
        <Column alignItems="stretch" gap={'1.5rem'} style={{ flexGrow: 0 }}>
          <Row gap="0.5rem">
            <Avatar
              size="small"
              image={authorAvatar}
              labelSource={authorName}
            />
            <Column gap="0.25rem">
              <Text.Medium isBold={true}>{authorName}</Text.Medium>
              <Text.Small style={{ color: 'var(--gray-300)' }}>
                {dateFormat(creationDate)}
              </Text.Small>
            </Column>
          </Row>
          <Text.Medium>{content}</Text.Medium>
          {imageFiles.length > 0 && (
            <Row
              gap={'0.5rem'}
              style={{
                flexWrap: 'wrap',
              }}
            >
              {imageFiles.map((file) => (
                <Image
                  key={file.id}
                  src={file.src}
                  resizeWidth={192}
                  alt={file.name}
                  onClick={() => onImageClick(file.src)}
                  style={{
                    width: '12rem',
                    height: '9rem',
                    objectFit: 'cover',
                    borderRadius: '0.75rem',
                    cursor: 'zoom-in',
                    border: 'var(--blue-border)',
                  }}
                />
              ))}
            </Row>
          )}
          {documentFiles.length > 0 && (
            <Row
              gap={'0.5rem'}
              style={{
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              {documentFiles.map((file) => (
                <FileDisplay
                  key={file.id}
                  type="document"
                  name={file.name}
                  src={file.src}
                  onRemove={undefined}
                />
              ))}
            </Row>
          )}
        </Column>
        {!isFirst && (
          <hr className="c6 b9 c7 bg"
          />
        )}
      </styled.div>
    )
  },
)
